import Icon from "@components/Atoms/Icon";
import useWatch from "@core-ui/hooks/useWatch";
import t from "@ext/localization/locale/translate";
import {
	CustomInputRenderer,
	getInputComponent,
	type InputValue,
} from "@ext/properties/components/Helpers/CustomInputRenderer";
import PropertiesScrollContainer from "@ext/properties/components/Helpers/PropertiesScrollContainer";
import PropertyButtons from "@ext/properties/components/Helpers/PropertyButtons";
import getFormatValue from "@ext/properties/logic/getFormatValue";
import { isManyProperty, type Property as PropertyType } from "@ext/properties/models";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@ui-kit/Dropdown";
import { memo, useCallback, useMemo, useState } from "react";

interface PropertyArticleProps {
	trigger: JSX.Element;
	property: PropertyType;
	disabled?: boolean;
	canDelete?: boolean;
	hideClear?: boolean;
	renderInput?: ({ value, onChange }: { value: string; onChange: (value: InputValue) => void }) => JSX.Element;
	onSubmit: (propertyId: string, value: string, isDelete?: boolean) => void;
	onDelete?: (propertyId: string) => void;
}

const PropertyArticle = memo((props: PropertyArticleProps) => {
	const { disabled, trigger, property, onSubmit, onDelete, canDelete = true, renderInput, hideClear } = props;
	const [value, setValue] = useState<string[] | string>(property.value);

	const InputComponent = renderInput || getInputComponent(property.type);

	useWatch(() => {
		if (InputComponent) setValue(property.value?.[0] ?? "");
	}, [property.value]);

	const onChange = useCallback(
		(incomingValue: InputValue) => {
			if (!InputComponent) return onSubmit(property.id, getFormatValue(incomingValue), false);
			setValue((prevValue) => {
				const isArray = typeof incomingValue !== "string" && Array.isArray(incomingValue);
				const formattedValue = isArray
					? [...prevValue, getFormatValue(incomingValue)]
					: getFormatValue(incomingValue);
				return formattedValue;
			});
		},
		[property.id, onSubmit, InputComponent],
	);

	const onOpenChange = useCallback(
		(open: boolean) => {
			if (open || renderInput || !InputComponent) return;
			onSubmit(property.id, getFormatValue(value), false);
		},
		[property.id, onSubmit, value, renderInput, InputComponent],
	);

	const deleteProperty = () => {
		if (onDelete) onDelete(property.id);
		else onSubmit(property.id, undefined, true);
	};

	const buttons = useMemo(() => {
		return (
			<PropertyButtons
				name={property.name}
				onChange={onChange}
				type={isManyProperty[property.type] ? "checkbox" : "radio"}
				value={property.value}
				values={property.values}
			/>
		);
	}, [property, onChange]);

	const getInputRenderer = useCallback(() => {
		if (renderInput) return renderInput({ value: typeof value === "string" ? value : value?.[0], onChange });
		return <CustomInputRenderer onChange={onChange} type={property.type} value={value} />;
	}, [property.type, value, onChange, renderInput]);

	return (
		<DropdownMenu onOpenChange={onOpenChange}>
			<DropdownMenuTrigger asChild disabled={disabled}>
				{trigger}
			</DropdownMenuTrigger>
			<DropdownMenuContent align="start">
				<DropdownMenuLabel>{property.name}</DropdownMenuLabel>
				<DropdownMenuSeparator />
				<PropertiesScrollContainer>{buttons}</PropertiesScrollContainer>
				{InputComponent && getInputRenderer()}
				{!hideClear && (property?.values?.length > 0 || InputComponent) && <DropdownMenuSeparator />}
				{canDelete && !hideClear && (
					<DropdownMenuItem onSelect={deleteProperty}>
						<Icon code="eraser" />
						{t("clear")}
					</DropdownMenuItem>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
});

export default PropertyArticle;
